import { useAutoneTranslation } from '@autone/translations';
import { capitalizeWords } from '@autone/utils';
import { AccordionContent } from '@radix-ui/react-accordion';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  forwardRef,
  type HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button, Checkbox } from '../../../atoms';
import { RippleRoot } from '../../../atoms/RippleRoot';
import { cn } from '../../../utils';
import { SearchInput } from '../../search-input';

import { useGeneralFiltersContext } from './GeneralFilters.context';
import { type GeneralFilterGroup, type GeneralFilterOption } from './types';

// --------------------
// Select All / Clear All Button for Filter Group
// --------------------
interface FilterGroupSelectAllButtonProps {
  id: string;
  filterGroupId: string;
  visibleOptions: GeneralFilterOption[];
  className?: string;
}

export const FilterGroupSelectAllButton = ({
  id,
  filterGroupId,
  visibleOptions,
  className,
}: FilterGroupSelectAllButtonProps) => {
  const { common } = useAutoneTranslation();
  const { toggleFilterGroup, toggledOptions } = useGeneralFiltersContext();

  const allVisibleOptionsSelected = useMemo(() => {
    if (visibleOptions.length === 0) return false;
    return visibleOptions.every(
      (option) => toggledOptions?.[filterGroupId]?.[option.id] === true,
    );
  }, [visibleOptions, filterGroupId, toggledOptions]);

  const label = allVisibleOptionsSelected
    ? common('clear-all')
    : common('select-all');

  return (
    <Button
      id={id}
      aria-label={label}
      className={className}
      variant="ghost"
      onClick={() =>
        toggleFilterGroup(
          filterGroupId,
          !allVisibleOptionsSelected,
          visibleOptions.map((o) => o.id),
        )
      }
    >
      {label}
    </Button>
  );
};

// --------------------
// Clear All Button
// --------------------
interface ClearAllButtonProps {
  id: string;
  className?: string;
}

export const ClearAllButton = ({ id, className }: ClearAllButtonProps) => {
  const { common } = useAutoneTranslation();
  const { setToggledOptions, toggledOptions } = useGeneralFiltersContext();

  const hasAnyFiltersSelected = useMemo(() => {
    for (const groupKey in toggledOptions) {
      const group = toggledOptions[groupKey];
      for (const optionKey in group) {
        if (group[optionKey]) {
          return true;
        }
      }
    }
    return false;
  }, [toggledOptions]);

  const handleClearAll = () => {
    setToggledOptions({});
  };

  return (
    <Button
      id={id}
      aria-label={common('clear-all')}
      className={className}
      variant="ghost"
      disabled={!hasAnyFiltersSelected}
      onClick={handleClearAll}
    >
      {common('clear-all')}
    </Button>
  );
};

// --------------------
// Accordion Content for Filter Group Options
// --------------------
interface GeneralFiltersGroupOptionsProps {
  filterGroup: GeneralFilterGroup;
}

export const GeneralFiltersGroupOptions = ({
  filterGroup,
}: GeneralFiltersGroupOptionsProps) => {
  const { common } = useAutoneTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const { id: filterGroupId, options } = filterGroup;
  const visibleOptions = useMemo(
    () =>
      options.filter((option) =>
        option.translatedDisplayName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  );

  return (
    <AccordionContent className="flex flex-col h-full min-h-0 gap-3 pt-3">
      <div className="flex flex-row items-center gap-2 px-2">
        <SearchInput
          id={`search-input-${filterGroupId}`}
          value={searchTerm}
          onChange={(search) => setSearchTerm(search)}
          placeholder={common('search-placeholder')}
        />
        <FilterGroupSelectAllButton
          id={`select-all-button-${filterGroupId}`}
          filterGroupId={filterGroup.id}
          visibleOptions={visibleOptions}
        />
      </div>
      <GeneralFiltersOptionsList
        filterGroup={filterGroup}
        visibleOptions={visibleOptions}
      />
    </AccordionContent>
  );
};

// --------------------
// Virtualized list of filter options
// --------------------
interface GeneralFiltersOptionsListProps {
  visibleOptions: GeneralFilterOption[];
  filterGroup: GeneralFilterGroup;
}

const GeneralFiltersOptionsList = ({
  filterGroup,
  visibleOptions,
}: GeneralFiltersOptionsListProps) => {
  const { common } = useAutoneTranslation();
  const { id: filterGroupId } = filterGroup;

  const parentRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: visibleOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 42,
    overscan: 50,
  });

  if (visibleOptions.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-6 px-4 text-center">
        <p className="text-muted-foreground text-sm">{common('no-results')}</p>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className="grow min-h-0 -mb-3 pb-3 overflow-y-scroll no-scrollbar"
    >
      <ul
        className="relative w-full"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const option = visibleOptions[virtualRow.index];
          if (!option) return null;

          return (
            <GeneralFiltersOption
              key={option.id}
              groupId={filterGroupId}
              option={option}
              style={{
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            />
          );
        })}
      </ul>
    </div>
  );
};

// --------------------
// Single Filter Option
// --------------------
interface GeneralFiltersOptionProps extends HTMLAttributes<HTMLLIElement> {
  groupId: string;
  option: GeneralFilterOption;
}

export const GeneralFiltersOption = forwardRef<
  HTMLLIElement,
  GeneralFiltersOptionProps
>(({ groupId, option, className, ...props }, ref) => {
  const { toggleOption, toggledOptions } = useGeneralFiltersContext();
  const optionIsSelected = toggledOptions?.[groupId]?.[option.id] || false;

  const toggleFilterOption = useCallback(
    () => toggleOption(groupId, option.id, !optionIsSelected),
    [toggleOption, groupId, option.id, optionIsSelected],
  );

  return (
    <li
      key={option.id}
      className={cn(
        'absolute overflow-hidden flex flex-row justify-start items-center hover:cursor-pointer hover:bg-accent gap-2 px-2',
        className,
      )}
      onClick={toggleFilterOption}
      {...props}
      ref={ref}
    >
      <RippleRoot
        className="bg-secondary-foreground"
        pulseDuration={0.4}
        pulseOpacity={0.3}
      />
      <div className="grid place-items-center h-full">
        <Checkbox
          id={`filter-item-checkbox-${option.id}}`}
          checked={optionIsSelected}
        />
      </div>

      <span className="font-medium">
        {capitalizeWords(option.translatedDisplayName)}
      </span>
    </li>
  );
});

// --------------------
// Empty state
// --------------------
export const GeneralFiltersNoOptions = () => {
  const { common } = useAutoneTranslation();
  return (
    <div className="flex flex-col justify-center items-center self-stretch w-full gap-1">
      <p className="text-muted-foreground text-lg font-medium">
        {common('filters.general-filters-no-options.header')}
      </p>
      <p className="text-muted-foreground">
        {common('filters.general-filters-no-options.subtitle')}
      </p>
    </div>
  );
};
